import { useCallback, useEffect, useRef, useState } from 'react';
import type { ConsoleApi, ConsoleConnection, ConsolePhoneNumber } from './console-api.js';
import { ConsoleApiError } from './console-api.js';
import type { MetaSignupClient } from './meta-signup.js';
import { useOnboarding } from './use-onboarding.jsx';

export type ConnectStatus = 'ocioso' | 'conectando' | 'erro';
export type NumbersStatus = 'ocioso' | 'carregando' | 'pronto' | 'erro';
export type ActivationStatus = 'ocioso' | 'ativando' | 'erro';

export interface WhatsappConnectionController {
  connectStatus: ConnectStatus;
  connectError: string | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;

  numbers: ConsolePhoneNumber[];
  numbersStatus: NumbersStatus;
  numbersError: string | null;
  loadNumbers: () => Promise<void>;
  selectNumber: (number: ConsolePhoneNumber) => Promise<boolean>;

  activationStatus: ActivationStatus;
  activationError: string | null;
  activationMissing: string[];
  activate: () => Promise<void>;
}

export interface UseWhatsappConnectionInput {
  api: ConsoleApi | null;
  signup: MetaSignupClient | null;
}

export function useWhatsappConnection(
  input: UseWhatsappConnectionInput,
): WhatsappConnectionController {
  const { setConnection } = useOnboarding();
  const [connectStatus, setConnectStatus] = useState<ConnectStatus>('ocioso');
  const [connectError, setConnectError] = useState<string | null>(null);
  const [numbers, setNumbers] = useState<ConsolePhoneNumber[]>([]);
  // Independent of the connection status: a failed listing must not read as "disconnected".
  const [numbersStatus, setNumbersStatus] = useState<NumbersStatus>('ocioso');
  const [numbersError, setNumbersError] = useState<string | null>(null);
  const [activationStatus, setActivationStatus] = useState<ActivationStatus>('ocioso');
  const [activationError, setActivationError] = useState<string | null>(null);
  const [activationMissing, setActivationMissing] = useState<string[]>([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const connect = useCallback(async () => {
    if (connectStatus === 'conectando') {
      return;
    }

    if (!input.api || !input.signup) {
      setConnectError('A conexão com a Meta está indisponível no momento.');
      setConnectStatus('erro');

      return;
    }

    setConnectStatus('conectando');
    setConnectError(null);

    try {
      const outcome = await input.signup.launch();

      if (!mounted.current) {
        return;
      }

      if (outcome.kind === 'cancelled') {
        setConnectStatus('ocioso');

        return;
      }

      if (outcome.kind === 'error') {
        setConnectError(outcome.message);
        setConnectStatus('erro');

        return;
      }

      const connection = await input.api.exchangeWhatsappCode({
        code: outcome.result.code,
        wabaId: outcome.result.wabaId,
        phoneNumberId: outcome.result.phoneNumberId ?? undefined,
      });

      if (!mounted.current) {
        return;
      }

      setConnection(connection);
      setConnectStatus('ocioso');
    } catch (error) {
      if (!mounted.current) {
        return;
      }

      setConnectError(errorMessage(error));
      setConnectStatus('erro');
    }
  }, [connectStatus, input.api, input.signup, setConnection]);

  const disconnect = useCallback(async () => {
    if (!input.api) {
      return;
    }

    try {
      await input.api.disconnectWhatsapp();

      if (!mounted.current) {
        return;
      }

      setConnection(null);
      setNumbers([]);
      setNumbersStatus('ocioso');
      setConnectStatus('ocioso');
      setConnectError(null);
    } catch (error) {
      if (!mounted.current) {
        return;
      }

      setConnectError(errorMessage(error));
    }
  }, [input.api, setConnection]);

  const loadNumbers = useCallback(async () => {
    if (!input.api) {
      setNumbersError('A conexão com a Marketside está indisponível no momento.');
      setNumbersStatus('erro');

      return;
    }

    setNumbersStatus('carregando');
    setNumbersError(null);

    try {
      const loaded = await input.api.listWhatsappNumbers();

      if (!mounted.current) {
        return;
      }

      setNumbers(loaded);
      setNumbersStatus('pronto');
    } catch (error) {
      if (!mounted.current) {
        return;
      }

      setNumbersError(errorMessage(error));
      setNumbersStatus('erro');
    }
  }, [input.api]);

  const selectNumber = useCallback(
    async (number: ConsolePhoneNumber) => {
      if (!input.api) {
        return false;
      }

      setNumbersError(null);

      try {
        const connection = await input.api.selectWhatsappNumber({
          phoneNumberId: number.id,
          displayPhoneNumber: number.displayPhoneNumber,
          verifiedName: number.verifiedName,
        });

        if (!mounted.current) {
          return false;
        }

        if (connection?.phoneNumberId) {
          setConnection(withSelectedPhoneNumberDetails(connection, number));

          return true;
        }

        setNumbersError('Não conseguimos confirmar este número. Tente novamente.');

        return false;
      } catch (error) {
        if (!mounted.current) {
          return false;
        }

        setNumbersError(errorMessage(error));

        return false;
      }
    },
    [input.api, setConnection],
  );

  const activate = useCallback(async () => {
    if (!input.api || activationStatus === 'ativando') {
      return;
    }

    setActivationStatus('ativando');
    setActivationError(null);
    setActivationMissing([]);

    try {
      const result = await input.api.activateWhatsapp();

      if (!mounted.current) {
        return;
      }

      setConnection(result.connection);
      setActivationStatus('ocioso');
    } catch (error) {
      if (!mounted.current) {
        return;
      }

      setActivationMissing(missingFrom(error));
      setActivationError(errorMessage(error));
      setActivationStatus('erro');
    }
  }, [activationStatus, input.api, setConnection]);

  return {
    connectStatus,
    connectError,
    connect,
    disconnect,
    numbers,
    numbersStatus,
    numbersError,
    loadNumbers,
    selectNumber,
    activationStatus,
    activationError,
    activationMissing,
    activate,
  };
}

function withSelectedPhoneNumberDetails(
  connection: ConsoleConnection,
  number: ConsolePhoneNumber,
): ConsoleConnection {
  return {
    ...connection,
    displayPhoneNumber: displayPhoneNumberForConnection(connection, number),
    verifiedName: verifiedNameForConnection(connection, number),
  };
}

function displayPhoneNumberForConnection(
  connection: ConsoleConnection,
  number: ConsolePhoneNumber,
): string | null {
  if (connection.displayPhoneNumber) {
    return connection.displayPhoneNumber;
  }

  return number.displayPhoneNumber;
}

function verifiedNameForConnection(
  connection: ConsoleConnection,
  number: ConsolePhoneNumber,
): string | null {
  if (connection.verifiedName) {
    return connection.verifiedName;
  }

  return number.verifiedName;
}

function errorMessage(error: unknown): string {
  if (error instanceof ConsoleApiError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Não foi possível concluir a ação. Tente novamente.';
}

function missingFrom(error: unknown): string[] {
  if (!(error instanceof ConsoleApiError)) {
    return [];
  }

  if (error.detail !== 'onboarding incomplete') {
    return [];
  }

  return ['store'];
}
